/**
 * Aggregator contracts (M5 task 1; 03-modules.md §13, 04-data-model.md aggregators,
 * 05-api.md "Aggregators", 06-flows.md F5, F6 step 2; 07-connectors.md werafood).
 *
 * Swiggy and Zomato orders arrive through the WeraFood connector as standard events
 * (vendor formats stop at the connector, CLAUDE.md rule 9). This file holds what the
 * module serves and takes: stores (outlet x platform x WeraFood merchant), the item
 * map, the auto-accept rule, aggregator orders, menu pushes, the availability sync
 * log, store status and its log, support actions, settlements, and the events.
 *
 * Personal data: an aggregator order keeps the customer's first name only (for the
 * packing slip) and never a phone or address; events and rooms carry none.
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { paiseSchema } from './money.js';

// ---------------------------------------------------------------------------
// Vocabulary

export const AGGREGATOR_PLATFORM_LIST = ['swiggy', 'zomato'] as const;
export const aggregatorPlatformValueSchema = z.enum(AGGREGATOR_PLATFORM_LIST);
export type AggregatorPlatformValue = (typeof AGGREGATOR_PLATFORM_LIST)[number];
export const PLATFORM_LABEL: Readonly<Record<AggregatorPlatformValue, string>> = {
  swiggy: 'Swiggy',
  zomato: 'Zomato',
};

export const STORE_STATUSES = ['active', 'inactive'] as const;
export const STORE_ONLINE_STATES = ['online', 'offline', 'unknown'] as const;
export type StoreOnlineState = (typeof STORE_ONLINE_STATES)[number];
/** Why a store is offline: staff took it, the heartbeat rule did, or the platform reported it. */
export const STORE_OFFLINE_REASONS = ['manual', 'heartbeat', 'platform'] as const;

/** `aggregator_order.status`. */
export const AGGREGATOR_ORDER_STATUSES = [
  /** Items the outlet has not mapped: no order was placed; fix the map, then retry. */
  'needs_mapping',
  /** Placed in orders, waiting for the outlet to accept. */
  'placed',
  'accepted',
  'rejected',
  /** The platform cancelled it. */
  'cancelled',
] as const;
export type AggregatorOrderStatus = (typeof AGGREGATOR_ORDER_STATUSES)[number];

/** The state of the call that told the platform what the outlet decided. */
export const ORDER_ACK_STATES = ['none', 'pending', 'sent', 'failed'] as const;

export const MAP_TARGET_TYPES = ['product', 'variant', 'addon'] as const;
export type MapTargetType = (typeof MAP_TARGET_TYPES)[number];

/** Status of a menu push, an availability sync or a store-status request. */
export const PUSH_STATUSES = ['queued', 'sent', 'succeeded', 'failed'] as const;
export type PushStatusValue = (typeof PUSH_STATUSES)[number];

/** Reasons an outlet rejects a platform order with; the connector maps them to the platform's list. */
export const AGGREGATOR_REJECT_REASONS = [
  { code: 'item_out_of_stock', label: 'An item is out of stock' },
  { code: 'store_closed', label: 'The kitchen is closed' },
  { code: 'too_busy', label: 'Too busy to take more orders' },
  { code: 'cannot_deliver', label: 'Cannot serve that address' },
  { code: 'other', label: 'Another reason' },
] as const;

export const SUPPORT_ACTION_KIND_LIST = [
  'delay_order',
  'customer_unreachable',
  'item_unavailable',
  'rider_delayed',
  'other',
] as const;
export const supportActionKindValueSchema = z.enum(SUPPORT_ACTION_KIND_LIST);
export const SUPPORT_ACTION_LABEL: Readonly<
  Record<(typeof SUPPORT_ACTION_KIND_LIST)[number], string>
> = {
  delay_order: 'Ask the platform to delay the order',
  customer_unreachable: 'Customer not reachable',
  item_unavailable: 'An item became unavailable',
  rider_delayed: 'Rider is late',
  other: 'Something else',
};

/** Opening-hours heartbeat rule (06-flows.md F5): no POS heartbeat for this long -> store offline. */
export const HEARTBEAT_STALE_SECONDS = 180;
export const MAX_ITEM_MAP_ENTRIES = 500;

const platform = aggregatorPlatformValueSchema;
const archivable = {
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
};

export const outletPlatformParamsSchema = z.object({ outletId: uuidSchema, platform });

// ---------------------------------------------------------------------------
// Stores

export const aggregatorStoreSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  platform,
  /** The store's id at WeraFood (the merchant id its webhooks carry). */
  weraMerchantId: z.string(),
  connectorInstallId: uuidSchema,
  status: z.enum(STORE_STATUSES),
  onlineState: z.enum(STORE_ONLINE_STATES),
  /** Set while the store is offline. */
  offlineReason: z.enum(STORE_OFFLINE_REASONS).nullable(),
  lastStatusAt: isoTimestampSchema.nullable(),
  ...archivable,
});
export type AggregatorStoreView = z.output<typeof aggregatorStoreSchema>;
export const aggregatorStoreListSchema = z.object({ data: z.array(aggregatorStoreSchema) });

const merchantIdSchema = z.string().trim().min(1).max(100);
export const createAggregatorStoreSchema = z.object({
  id: uuidSchema.optional(),
  platform,
  weraMerchantId: merchantIdSchema,
  connectorInstallId: uuidSchema,
});
export const updateAggregatorStoreSchema = z
  .object({
    weraMerchantId: merchantIdSchema,
    connectorInstallId: uuidSchema,
    status: z.enum(STORE_STATUSES),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });
export const aggregatorStoreParamsSchema = z.object({ outletId: uuidSchema, storeId: uuidSchema });

// ---------------------------------------------------------------------------
// Item map

export const itemMapEntrySchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  platform,
  targetType: z.enum(MAP_TARGET_TYPES),
  targetId: uuidSchema,
  /** The id the platform uses for it (menu pushes use our own ids, so usually the same). */
  externalId: z.string(),
  /** What the target is called now (null when it no longer exists). */
  targetName: z.string().nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
});
export type ItemMapEntryView = z.output<typeof itemMapEntrySchema>;
export const itemMapQuerySchema = z.object({
  platform: platform.optional(),
  targetType: z.enum(MAP_TARGET_TYPES).optional(),
});
export const itemMapListSchema = z.object({ data: z.array(itemMapEntrySchema) });
export const setItemMapSchema = z.object({
  platform,
  entries: z
    .array(
      z.object({
        targetType: z.enum(MAP_TARGET_TYPES),
        targetId: uuidSchema,
        externalId: z.string().trim().min(1).max(200),
      }),
    )
    .min(1)
    .max(MAX_ITEM_MAP_ENTRIES),
});
export const itemMapParamsSchema = z.object({ outletId: uuidSchema, entryId: uuidSchema });

// ---------------------------------------------------------------------------
// Auto-accept rule

export const autoAcceptRuleSchema = z.object({
  outletId: uuidSchema,
  /** Accept new platform orders on the server when the guards below hold. */
  enabled: z.boolean(),
  /** Not while this many platform orders are already open (the kitchen's capacity). */
  maxOpenOrders: z.int().min(1).max(200),
  /** Preparation time told to the platform on accept, minutes (legacy default 45). */
  prepMinutes: z.int().min(5).max(240),
  /** Send accepted orders to the kitchen at once (KOT with packaging note and source). */
  sendToKitchen: z.boolean(),
  /** Take the stores offline when no POS heartbeat arrives for 3 minutes in opening hours. */
  heartbeatGuard: z.boolean(),
  /** The timing group that is the outlet's opening hours; null = open all day. */
  openingTimingGroupId: uuidSchema.nullable(),
  /** False until someone saved the rule: `enabled` is the default. */
  isDefault: z.boolean(),
  updatedAt: isoTimestampSchema.nullable(),
  version: z.int(),
});
export type AutoAcceptRuleView = z.output<typeof autoAcceptRuleSchema>;
export const saveAutoAcceptRuleSchema = z
  .object({
    enabled: z.boolean(),
    maxOpenOrders: autoAcceptRuleSchema.shape.maxOpenOrders,
    prepMinutes: autoAcceptRuleSchema.shape.prepMinutes,
    sendToKitchen: z.boolean(),
    heartbeatGuard: z.boolean(),
    openingTimingGroupId: uuidSchema.nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });
export const AUTO_ACCEPT_DEFAULTS = {
  enabled: false,
  maxOpenOrders: 10,
  prepMinutes: 45,
  sendToKitchen: true,
  heartbeatGuard: true,
  openingTimingGroupId: null,
} as const;

// ---------------------------------------------------------------------------
// Aggregator orders

export const unmappedItemSchema = z.object({
  externalItemId: z.string(),
  name: z.string(),
  /** Why the item could not be placed: not mapped, not on the menu for this channel, no price… */
  reason: z.string(),
});

export const aggregatorOrderSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  platform,
  /** The platform's order id (what the customer and the rider call it). */
  externalId: z.string(),
  weraOrderId: z.string().nullable(),
  /** The order in `orders`, once placed. */
  orderId: uuidSchema.nullable(),
  orderNo: z.int().nullable(),
  status: z.enum(AGGREGATOR_ORDER_STATUSES),
  acceptedBy: z.enum(['auto', 'staff']).nullable(),
  /** The state of the accept / reject / food-ready call to the platform. */
  ackState: z.enum(ORDER_ACK_STATES),
  ackError: z.string().nullable(),
  customerName: z.string().nullable(),
  fulfilment: z.enum(['delivery', 'pickup']),
  payment: z.enum(['prepaid', 'cod']),
  /** What the platform says the customer paid. */
  platformTotalPaise: paiseSchema,
  /** What our pricing makes of the same items (null before the order is placed). */
  ourTotalPaise: paiseSchema.nullable(),
  /** platform - ours; logged, never blocks the order. */
  discrepancyPaise: paiseSchema.nullable(),
  merchantDiscountPaise: paiseSchema,
  aggregatorDiscountPaise: paiseSchema,
  packagingPaise: paiseSchema,
  deliveryPaise: paiseSchema,
  taxPaise: paiseSchema,
  prepMinutes: z.int().nullable(),
  /** Items the outlet has to map before the order can be placed. */
  unmapped: z.array(unmappedItemSchema),
  rider: z.object({ name: z.string(), phone: z.string().nullable() }).nullable(),
  cancelReason: z.string().nullable(),
  requestId: z.string().nullable(),
  receivedAt: isoTimestampSchema,
  acceptedAt: isoTimestampSchema.nullable(),
  cancelledAt: isoTimestampSchema.nullable(),
  /** Settlement import: which payout line matched it. */
  settled: z.boolean(),
});
export type AggregatorOrderView = z.output<typeof aggregatorOrderSchema>;

export const aggregatorOrderQuerySchema = z.object({
  platform: platform.optional(),
  status: z.enum(AGGREGATOR_ORDER_STATUSES).optional(),
  limit: z.preprocess(
    (v) => (typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v.trim()) : v),
    z.number().int().min(1).max(200).default(50),
  ),
  offset: z.preprocess(
    (v) => (typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v.trim()) : v),
    z.number().int().min(0).max(1_000_000).default(0),
  ),
});
export const aggregatorOrderListSchema = z.object({
  data: z.array(aggregatorOrderSchema),
  total: z.int(),
  nextOffset: z.int().nullable(),
});
export const aggregatorOrderParamsSchema = z.object({
  outletId: uuidSchema,
  aggregatorOrderId: uuidSchema,
});

export const supportActionSchema = z.object({
  id: uuidSchema,
  aggregatorOrderId: uuidSchema,
  kind: supportActionKindValueSchema,
  note: z.string().nullable(),
  status: z.enum(PUSH_STATUSES),
  requestId: z.string().nullable(),
  error: z.string().nullable(),
  requestedAt: isoTimestampSchema,
  completedAt: isoTimestampSchema.nullable(),
});
export type SupportActionView = z.output<typeof supportActionSchema>;
export const raiseSupportActionSchema = z.object({
  id: uuidSchema.optional(),
  kind: supportActionKindValueSchema,
  note: z.string().trim().max(500).optional(),
});

// ---------------------------------------------------------------------------
// Menu push (F1 step 5)

export const menuPushSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  platform,
  /** The published menu version that was pushed. */
  menuVersion: z.int(),
  status: z.enum(PUSH_STATUSES),
  /** The platform's request id; track it to see whether items were refused. */
  requestId: z.string().nullable(),
  itemCount: z.int(),
  /** Items the platform refused (after tracking), or why the call failed. */
  errors: z.array(z.object({ itemId: z.string(), message: z.string() })),
  error: z.string().nullable(),
  attempts: z.int(),
  requestedBy: uuidSchema.nullable(),
  requestedAt: isoTimestampSchema,
  pushedAt: isoTimestampSchema.nullable(),
  completedAt: isoTimestampSchema.nullable(),
});
export type MenuPushView = z.output<typeof menuPushSchema>;
export const menuPushListSchema = z.object({
  data: z.array(menuPushSchema),
  nextOffset: z.int().nullable(),
});
export const pushListQuerySchema = z.object({
  platform: platform.optional(),
  status: z.enum(PUSH_STATUSES).optional(),
  limit: aggregatorOrderQuerySchema.shape.limit,
  offset: aggregatorOrderQuerySchema.shape.offset,
});
export const pushParamsSchema = z.object({ outletId: uuidSchema, pushId: uuidSchema });
/** `POST {o}/aggregators/{platform}/menu-push`: nothing to say; the published menu is pushed. */
export const menuPushRequestSchema = z.object({ id: uuidSchema.optional() });

// ---------------------------------------------------------------------------
// Availability sync log (F6 step 2)

export const availabilitySyncSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  platform,
  targets: z.array(
    z.object({
      type: z.enum(MAP_TARGET_TYPES),
      id: uuidSchema,
      /** The name at the time (so the log reads well after the item is renamed). */
      name: z.string().nullable(),
    }),
  ),
  available: z.boolean(),
  until: isoTimestampSchema.nullable(),
  status: z.enum(PUSH_STATUSES),
  requestId: z.string().nullable(),
  error: z.string().nullable(),
  attempts: z.int(),
  /** The `menu.setAvailability` event this answers. */
  eventId: uuidSchema,
  requestedAt: isoTimestampSchema,
  completedAt: isoTimestampSchema.nullable(),
});
export type AvailabilitySyncView = z.output<typeof availabilitySyncSchema>;
export const availabilitySyncListSchema = z.object({
  data: z.array(availabilitySyncSchema),
  nextOffset: z.int().nullable(),
});
export const syncParamsSchema = z.object({ outletId: uuidSchema, syncId: uuidSchema });

// ---------------------------------------------------------------------------
// Store status (F5 heartbeat rule, manual toggle)

export const STORE_STATUS_REASONS = ['manual', 'heartbeat', 'heartbeat_return'] as const;
export const storeStatusLogSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  platform,
  desired: z.enum(['online', 'offline']),
  reason: z.enum(STORE_STATUS_REASONS),
  status: z.enum(PUSH_STATUSES),
  error: z.string().nullable(),
  requestedBy: uuidSchema.nullable(),
  requestedAt: isoTimestampSchema,
  completedAt: isoTimestampSchema.nullable(),
});
export type StoreStatusLogView = z.output<typeof storeStatusLogSchema>;
export const storeStatusLogListSchema = z.object({
  data: z.array(storeStatusLogSchema),
  nextOffset: z.int().nullable(),
});
export const setStoreStatusSchema = z.object({
  id: uuidSchema.optional(),
  status: z.enum(['online', 'offline']),
});

// ---------------------------------------------------------------------------
// Settlements (F5 step 7, F9 step 3)

export const SETTLEMENT_ROW_STATUSES = ['matched', 'mismatch', 'unmatched'] as const;
export type SettlementRowStatus = (typeof SETTLEMENT_ROW_STATUSES)[number];

const settlementTotals = {
  grossPaise: paiseSchema,
  merchantDiscountPaise: paiseSchema,
  aggregatorDiscountPaise: paiseSchema,
  commissionPaise: paiseSchema,
  taxOnCommissionPaise: paiseSchema,
  otherDeductionsPaise: paiseSchema,
  payoutPaise: paiseSchema,
};

export const settlementSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  platform,
  fileName: z.string().nullable(),
  fileSha256: z.string(),
  periodFrom: businessDateSchema.nullable(),
  periodTo: businessDateSchema.nullable(),
  rowCount: z.int(),
  matched: z.int(),
  mismatched: z.int(),
  unmatched: z.int(),
  ...settlementTotals,
  importedBy: uuidSchema.nullable(),
  importedAt: isoTimestampSchema,
});
export type SettlementView = z.output<typeof settlementSchema>;

export const settlementRowSchema = z.object({
  id: uuidSchema,
  line: z.int(),
  externalOrderId: z.string(),
  orderId: uuidSchema.nullable(),
  orderNo: z.int().nullable(),
  status: z.enum(SETTLEMENT_ROW_STATUSES),
  /** Why a row is a mismatch or unmatched, in plain words. */
  note: z.string().nullable(),
  orderDate: businessDateSchema.nullable(),
  ...settlementTotals,
  /** What the order says the platform should pay (items - merchant discount), when matched. */
  expectedPayoutPaise: paiseSchema.nullable(),
});
export type SettlementRowView = z.output<typeof settlementRowSchema>;

export const settlementDetailSchema = settlementSchema.extend({
  rows: z.array(settlementRowSchema),
});
export const settlementListSchema = z.object({
  data: z.array(settlementSchema),
  nextOffset: z.int().nullable(),
});
export const settlementListQuerySchema = z.object({
  platform: platform.optional(),
  limit: aggregatorOrderQuerySchema.shape.limit,
  offset: aggregatorOrderQuerySchema.shape.offset,
});
export const settlementParamsSchema = z.object({ outletId: uuidSchema, settlementId: uuidSchema });

/** The payout file as text (CSV, UTF-8), or base64 of it. Exactly one. */
export const importSettlementSchema = z
  .object({
    id: uuidSchema.optional(),
    platform,
    fileName: z.string().trim().max(200).optional(),
    content: z.string().max(5_000_000).optional(),
    contentBase64: z.string().max(7_000_000).optional(),
  })
  .refine((v) => (v.content === undefined) !== (v.contentBase64 === undefined), {
    message: 'Send the file as content or contentBase64',
  });
export const importSettlementResultSchema = z.object({
  settlement: settlementSchema,
  /** True when this exact file was imported before: nothing changed. */
  duplicate: z.boolean(),
});

// ---------------------------------------------------------------------------
// Overview (the back-office landing screen)

export const aggregatorOverviewSchema = z.object({
  stores: z.array(aggregatorStoreSchema),
  rule: autoAcceptRuleSchema,
  /** Platform orders waiting for the outlet. */
  awaitingAcceptance: z.int(),
  needsMapping: z.int(),
  failedPushes: z.int(),
  failedSyncs: z.int(),
  /** The last POS heartbeat of the outlet (null = none yet). */
  lastHeartbeatAt: isoTimestampSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Payments report: aggregator payouts vs orders (03-modules.md §9, report 4)

export const aggregatorPayoutRowSchema = z.object({
  platform,
  orders: z.int(),
  orderTotalPaise: paiseSchema,
  /** Orders that appear on an imported payout. */
  settledOrders: z.int(),
  payoutPaise: paiseSchema,
  merchantDiscountPaise: paiseSchema,
  aggregatorDiscountPaise: paiseSchema,
  commissionPaise: paiseSchema,
  /** Orders of the range that no payout has covered yet, and their value. */
  unsettledOrders: z.int(),
  unsettledOrderTotalPaise: paiseSchema,
  mismatchedRows: z.int(),
  unmatchedRows: z.int(),
});
export type AggregatorPayoutRow = z.output<typeof aggregatorPayoutRowSchema>;
export const aggregatorPayoutsSchema = z.object({ platforms: z.array(aggregatorPayoutRowSchema) });
export type AggregatorPayouts = z.output<typeof aggregatorPayoutsSchema>;

// ---------------------------------------------------------------------------
// Events (03-modules.md §13: aggregator.order_received, menu_pushed, push_failed,
// store_status_changed, plus the ones the flows need). No personal data: they are
// projected to the outlet-wide pos room.

const orderEventRef = {
  aggregatorOrderId: uuidSchema,
  platform,
  externalId: z.string(),
  orderId: uuidSchema.nullable(),
  orderNo: z.int().nullable(),
};

export const aggregatorOrderReceivedV1 = defineEvent(
  'aggregator.order_received',
  1,
  z.object({
    ...orderEventRef,
    status: z.enum(AGGREGATOR_ORDER_STATUSES),
    totalPaise: paiseSchema,
    lineCount: z.int(),
    unmappedCount: z.int(),
    /** The platform total minus ours, when the order was placed. */
    discrepancyPaise: paiseSchema.nullable(),
  }),
);

/** The server accepted (or staff accepted) the order and the platform call is due. */
export const aggregatorOrderAcceptedV1 = defineEvent(
  'aggregator.order_accepted',
  1,
  z.object({
    ...orderEventRef,
    acceptedBy: z.enum(['auto', 'staff']),
    prepMinutes: z.int(),
  }),
);

export const aggregatorOrderAutoAcceptSkippedV1 = defineEvent(
  'aggregator.auto_accept_skipped',
  1,
  z.object({
    ...orderEventRef,
    /** `disabled`, `no_heartbeat`, `outside_hours`, `capacity`, `store_offline`. */
    reason: z.string(),
  }),
);

/** The platform cancelled the order. */
export const aggregatorOrderCancelledV1 = defineEvent(
  'aggregator.order_cancelled',
  1,
  z.object({ ...orderEventRef, reason: z.string().nullable(), sentToKitchen: z.boolean() }),
);

export const aggregatorRiderAssignedV1 = defineEvent(
  'aggregator.rider_assigned',
  1,
  z.object({ ...orderEventRef, riderName: z.string() }),
);

/** The outlet rejected the order, or cancelled it after accepting it; the platform is told next. */
export const aggregatorOrderRejectedV1 = defineEvent(
  'aggregator.order_rejected',
  1,
  z.object({ ...orderEventRef, reasonCode: z.string() }),
);

/** A call to the platform about an order (accept, reject, food ready) finished. */
export const aggregatorOrderAckRecordedV1 = defineEvent(
  'aggregator.order_ack_recorded',
  1,
  z.object({
    ...orderEventRef,
    action: z.enum(['accept', 'reject', 'ready']),
    ok: z.boolean(),
    error: z.string().nullable(),
    requestId: z.string().nullable(),
  }),
);

export const aggregatorSupportActionRecordedV1 = defineEvent(
  'aggregator.support_action_recorded',
  1,
  z.object({
    ...orderEventRef,
    supportActionId: uuidSchema,
    kind: supportActionKindValueSchema,
    status: z.enum(PUSH_STATUSES),
  }),
);

const pushRef = {
  pushId: uuidSchema,
  platform,
  menuVersion: z.int(),
};
export const aggregatorMenuPushRequestedV1 = defineEvent(
  'aggregator.menu_push_requested',
  1,
  z.object({ ...pushRef, itemCount: z.int() }),
);
export const aggregatorMenuPushedV1 = defineEvent(
  'aggregator.menu_pushed',
  1,
  z.object({ ...pushRef, requestId: z.string().nullable(), status: z.enum(PUSH_STATUSES) }),
);
export const aggregatorPushFailedV1 = defineEvent(
  'aggregator.push_failed',
  1,
  z.object({
    ...pushRef,
    requestId: z.string().nullable(),
    error: z.string().nullable(),
    refusedItems: z.int(),
  }),
);

const syncRef = { syncId: uuidSchema, platform };
export const aggregatorAvailabilitySyncRequestedV1 = defineEvent(
  'aggregator.availability_sync_requested',
  1,
  z.object({ ...syncRef, eventId: uuidSchema, available: z.boolean(), targetCount: z.int() }),
);
export const aggregatorAvailabilitySyncedV1 = defineEvent(
  'aggregator.availability_synced',
  1,
  z.object({
    ...syncRef,
    requestId: z.string().nullable(),
    status: z.enum(PUSH_STATUSES),
    available: z.boolean(),
  }),
);
export const aggregatorAvailabilitySyncFailedV1 = defineEvent(
  'aggregator.availability_sync_failed',
  1,
  z.object({ ...syncRef, requestId: z.string().nullable(), error: z.string().nullable() }),
);

export const aggregatorStoreStatusChangedV1 = defineEvent(
  'aggregator.store_status_changed',
  1,
  z.object({
    storeId: uuidSchema,
    platform,
    onlineState: z.enum(STORE_ONLINE_STATES),
    reason: z.enum(STORE_STATUS_REASONS),
    statusLogId: uuidSchema,
  }),
);
export const aggregatorStoreStatusAppliedV1 = defineEvent(
  'aggregator.store_status_applied',
  1,
  z.object({
    statusLogId: uuidSchema,
    platform,
    desired: z.enum(['online', 'offline']),
    ok: z.boolean(),
    error: z.string().nullable(),
  }),
);

export const aggregatorStoreSavedV1 = defineEvent(
  'aggregator.store_saved',
  1,
  z.object({
    storeId: uuidSchema,
    platform,
    change: z.enum(['created', 'updated', 'archived']),
  }),
);
export const aggregatorItemMapSavedV1 = defineEvent(
  'aggregator.item_map_saved',
  1,
  z.object({ platform, entries: z.int(), removed: z.int() }),
);
export const aggregatorAutoAcceptRuleSavedV1 = defineEvent(
  'aggregator.auto_accept_rule_saved',
  1,
  z.object({ enabled: z.boolean(), maxOpenOrders: z.int(), prepMinutes: z.int() }),
);
export const aggregatorSettlementImportedV1 = defineEvent(
  'aggregator.settlement_imported',
  1,
  z.object({
    settlementId: uuidSchema,
    platform,
    rowCount: z.int(),
    matched: z.int(),
    mismatched: z.int(),
    unmatched: z.int(),
    payoutPaise: paiseSchema,
  }),
);

export const AGGREGATOR_EVENTS = [
  aggregatorOrderReceivedV1,
  aggregatorOrderAcceptedV1,
  aggregatorOrderAutoAcceptSkippedV1,
  aggregatorOrderCancelledV1,
  aggregatorOrderRejectedV1,
  aggregatorRiderAssignedV1,
  aggregatorOrderAckRecordedV1,
  aggregatorSupportActionRecordedV1,
  aggregatorMenuPushRequestedV1,
  aggregatorMenuPushedV1,
  aggregatorPushFailedV1,
  aggregatorAvailabilitySyncRequestedV1,
  aggregatorAvailabilitySyncedV1,
  aggregatorAvailabilitySyncFailedV1,
  aggregatorStoreStatusChangedV1,
  aggregatorStoreStatusAppliedV1,
  aggregatorStoreSavedV1,
  aggregatorItemMapSavedV1,
  aggregatorAutoAcceptRuleSavedV1,
  aggregatorSettlementImportedV1,
] as const;
